// app/javascript/components/DagDiagram.js
import React, { useEffect, useState } from 'react';

const DagDiagram = ({name, data, d3link, openModal, createNewNode}) => {
    // Add state to track selected node, selected link, and search text
    const [selectedNode, setSelectedNode] = useState(null);
    const [selectedLink, setSelectedLink] = useState(null);
    const [searchText, setSearchText] = useState('');
    
    function callback() {
        // Set diagram name
        var diagramNameElement = document.getElementById('diagramName');
        diagramNameElement.textContent = name;

        // Create a local variable to track selected node within the D3 context
        // This avoids issues with React state timing
        let localSelectedNode = null;
        let localSelectedLink = null;

        // Helper function to clear all selections
        function clearAllSelections() {
            // Clear node selection
            d3.selectAll('.node-selected')
                .classed('node-selected', false)
                .select('circle')
                .attr('stroke-width', 0);
                
            // Clear link selection
            d3.selectAll('.link-selected')
                .classed('link-selected', false)
                .attr('stroke-width', 3);
                
            // Remove delete button if it exists
            d3.select("#delete-link-button").remove();
            
            // Reset state variables
            localSelectedNode = null;
            localSelectedLink = null;
            setSelectedNode(null);
            setSelectedLink(null);
        }

        // Handler for link selection
        function handleLinkClick(event, d) {
            
            // Prevent event bubbling to background
            event.preventDefault();
            event.stopPropagation();
            
            // Clear any existing selections first
            clearAllSelections();
            
            // Select this link
            d3.select(event.currentTarget)
                .classed('link-selected', true)
                .attr('stroke-width', 5);
                
            // Store the selected link
            localSelectedLink = d;
            setSelectedLink(d);
            
            // Create delete button
            createDeleteButton(d);
        }

        // Function to create delete button for selected link
        function createDeleteButton(link) {
            // Remove any existing button
            d3.select("#delete-link-button").remove();
            
            // Calculate true visual midpoint of the path
            const linkPoints = link.points;
            
            // Better visual midpoint calculation
            let totalLength = 0;
            const segmentLengths = [];
            
            // Calculate the length of each segment and total path length
            for (let i = 1; i < linkPoints.length; i++) {
                const p1 = linkPoints[i-1];
                const p2 = linkPoints[i];
                const dx = p2[0] - p1[0];
                const dy = p2[1] - p1[1];
                const segmentLength = Math.sqrt(dx * dx + dy * dy);
                segmentLengths.push(segmentLength);
                totalLength += segmentLength;
            }
            
            // Find which segment contains the midpoint
            const halfLength = totalLength / 2;
            let lengthSoFar = 0;
            let segmentIndex = 0;
            let segmentPos = 0;
            
            for (let i = 0; i < segmentLengths.length; i++) {
                if (lengthSoFar + segmentLengths[i] >= halfLength) {
                    segmentIndex = i;
                    segmentPos = (halfLength - lengthSoFar) / segmentLengths[i];
                    break;
                }
                lengthSoFar += segmentLengths[i];
            }

            // Interpolate to find the exact midpoint
            const p1 = linkPoints[segmentIndex];
            const p2 = linkPoints[segmentIndex + 1];
            const midPoint = [
                p1[0] + segmentPos * (p2[0] - p1[0]),
                p1[1] + segmentPos * (p2[1] - p1[1])
            ];

            // Create button container
            const deleteButton = rootG.append("g")
                .attr("id", "delete-link-button")
                .attr("transform", `translate(${midPoint[0]}, ${midPoint[1]})`)
                .attr("class", "cursor-pointer")
                .on("click", (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    confirmLinkDeletion(link);
                });
                
            // Create button background
            deleteButton.append("circle")
                .attr("r", 15)
                .attr("fill", "white")
                .attr("stroke", "red")
                .attr("stroke-width", 2);
                
            // Create X mark
            deleteButton.append("text")
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "central")
                .attr("font-weight", "bold")
                .attr("fill", "red")
                .attr("font-size", "18px")
                .text("×");
                
            // Add hover effect
            deleteButton
                .on("mouseover", function() {
                    d3.select(this).select("circle").attr("fill", "#ffcccc");
                })
                .on("mouseout", function() {
                    d3.select(this).select("circle").attr("fill", "white");
                });
        }
        
        // Function to confirm link deletion
        function confirmLinkDeletion(link) {
            // Provide haptic feedback
            if (navigator.vibrate) navigator.vibrate(50);

            // Show confirmation dialog
            if (confirm("Are you sure you want to remove this connection?")) {
                deleteLinkConnection(link);
            }
        }
        
        // Function to actually delete the link
        function deleteLinkConnection(link) {
            try {
                // Get the CSRF token
                const csrfToken = document.querySelector("[name=csrf-token]").content;
                
                // Find the target node data in the original data array
                const targetNodeData = data.find(node => node.id === link.target.data.id);
                
                if (targetNodeData) {
                    // Create updated node data with the parent removed
                    const updatedParentIds = (targetNodeData.parentIds || []).filter(
                        id => id !== link.source.data.id
                    );
                    
                    const updatedNodeData = {
                        ...targetNodeData,
                        parentIds: updatedParentIds
                    };
                    
                    // Send API request to update the node
                    fetch("/dag_nodes/", {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRF-Token": csrfToken,
                        },
                        body: JSON.stringify(updatedNodeData),
                    })
                    .then(response => {
                        if (response.ok) {
                            console.log("✅ Connection deleted!");
                            // Reload the page to refresh the diagram
                            window.location.reload();
                        } else {
                            console.error("Failed to delete connection");
                            alert("Failed to delete connection");
                        }
                    })
                    .catch(error => {
                        console.error("Error:", error);
                        alert("Error removing connection");
                    });
                }

                // Clear the selection
                clearAllSelections();
            } catch (error) {
                console.error("Error deleting link:", error);
            }
        }

        // Setup of the graph
        function arrowTransform({ points }) {
            const [[x1, y1], [x2, y2]] = points.slice(-2);
            const angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI + 90;
            return `translate(${x2}, ${y2}) rotate(${angle})`;
        }

        const builder = d3dag.graphStratify();
        const graph = builder(data);

        // Compute Layout
        const nodeRadius = 100;
        const nodeSize = [nodeRadius * 2, nodeRadius * 2];
        const shape = d3dag.tweakShape(nodeSize, d3dag.shapeEllipse);
        const line = d3.line().curve(d3.curveMonotoneY);

        const layout = d3dag
            .sugiyama()
            .nodeSize(nodeSize)
            .gap([nodeRadius, nodeRadius])
            .tweaks([shape]);

        const { width, height } = layout(graph);

        // Rendering
        const steps = graph.nnodes() - 1;
        const interp = d3.interpolateRainbow;

        const svg = d3.select("#graph")
            .attr("width", "100%")
            .attr("height", "100%")
            .style("width", "100%")
            .style("height", "100%");
        
        // Clear any existing content
        svg.selectAll("*").remove();
        
        // Recreate the base group
        const rootG = svg.append("g");
        rootG.append("defs").attr("id", "defs");
        rootG.append("g").attr("id", "links");
        rootG.append("g").attr("id", "nodes");
        rootG.append("g").attr("id", "arrows");
        
        const trans = svg.transition().duration(750);

        function handleMouseOut(event, d) {
            if (!d || !d.data) return; // Guard against null d
            
            const circleId = `#circle-${d.data.id}`;
            const circle = svg.select(circleId);

            // Restore the original circle appearance if it's not the selected node
            if (!localSelectedNode || !localSelectedNode.data || localSelectedNode.data.id !== d.data.id) {
                circle.attr("fill", (n) => n.data.color);
            }
        }

        // Variables for double-click detection
        let clickTimer = null;
        let lastClickedNode = null;
        const doubleClickDelay = 300; // milliseconds
        
        function handleNodeClick(event, d) {
            if (!d || !d.data) return; // Guard against null d
            
            // Prevent event bubbling
            event.stopPropagation();
            
            if (lastClickedNode === d.data.id && clickTimer) {
                // Double-click detected
                clearTimeout(clickTimer);
                clickTimer = null;
                lastClickedNode = null;
                
                // Open modal on double-click
                openModal(null, d.data.id);
            } else {
                // First click - select the node and set timer
                lastClickedNode = d.data.id;
                
                // Clear previous selection
                d3.selectAll('.node-selected')
                    .classed('node-selected', false)
                    .select('circle')
                    .attr('stroke-width', 0);
                    
                // Set new selection
                d3.select(event.currentTarget)
                    .classed('node-selected', true)
                    .select('circle')
                    .attr('stroke', '#ffffff')
                    .attr('stroke-width', 4);
                    
                // Store selected node in both local variable and React state
                localSelectedNode = d;
                setSelectedNode(d);
                
                // Set timer for double-click detection
                clickTimer = setTimeout(() => {
                    clickTimer = null;
                }, doubleClickDelay);
            }
        }
        
        // Touch handling for double-tap
        let touchTimer = null;
        let lastTouchedNode = null;
        let touchDragging = false;
        
        function handleNodeTouch(event, d) {
            if (!d || !d.data) return; // Guard against null d
            
            // Prevent default to avoid scrolling/zooming
            event.preventDefault();
            event.stopPropagation(); // Stop propagation to prevent other handlers from firing
            
            // Clear previous selection
            d3.selectAll('.node-selected')
                .classed('node-selected', false)
                .select('circle')
                .attr('stroke-width', 0);
                
            // Set new selection with enhanced visibility for mobile
            d3.select(event.currentTarget)
                .classed('node-selected', true)
                .select('circle')
                .attr('stroke', '#3fc1ff') // Brighter cyan color for better mobile visibility
                .attr('stroke-width', 6);   // Thicker border for better visibility
            
            // Add vibration feedback if available
            if (navigator.vibrate) {
                navigator.vibrate(50); // Short vibration for tactile feedback
            }
                
            // Store selected node in both local variable and React state
            localSelectedNode = d;
            setSelectedNode(d);
            
            // Handle double-tap detection
            if (lastTouchedNode === d.data.id && touchTimer) {
                clearTimeout(touchTimer);
                touchTimer = null;
                lastTouchedNode = null;
                openModal(null, d.data.id);
                return;
            }
            
            lastTouchedNode = d.data.id;
            touchTimer = setTimeout(() => {
                touchTimer = null;
                // No visual indication needed anymore since we're prioritizing drag
            }, doubleClickDelay);
        }
        
        // More robust touch event handling
        function cancelTouch(event) {
            // Always prevent default for touchmove on nodes to prevent scrolling
            event.preventDefault();
        }
        
        // Set up the drag behavior for connecting nodes
        const dragHandler = d3.drag()
            .on("start", function(event, d) {
                // Track if an actual drag operation is happening
                this.dragActive = false;
                touchDragging = false;
                
                // For touch events
                if (event.sourceEvent.type.startsWith('touch')) {
                    // Prevent default to stop scrolling/zooming
                    event.sourceEvent.preventDefault();
                    
                    // Always select the current node first
                    d3.selectAll('.node-selected')
                        .classed('node-selected', false)
                        .select('circle')
                        .attr('stroke-width', 0);
                        
                    d3.select(this)
                        .classed('node-selected', true)
                        .select('circle')
                        .attr('stroke', '#3fc1ff')
                        .attr('stroke-width', 6);
                    
                    localSelectedNode = d;
                    setSelectedNode(d);
                    
                    // Store the start position for measuring drag distance
                    this.dragStartX = event.x;
                    this.dragStartY = event.y;
                    
                    // IMMEDIATELY create the connection line for touch devices
                    rootG.append("path")
                        .attr("id", "temp-connection")
                        .attr("d", `M${d.x},${d.y} L${d.x},${d.y}`)
                        .attr("stroke", d.data.color)
                        .attr("stroke-width", 3)
                        .attr("stroke-dasharray", "5,5")
                        .attr("fill", "none");
                    
                    // Set as active immediately for touch
                    this.dragActive = true;
                    touchDragging = true;
                } else {
                    // For mouse events, only allow dragging from selected node
                    if (localSelectedNode !== d) return;
                    
                    // Create a temporary line to visualize the connection
                    rootG.append("path")
                        .attr("id", "temp-connection")
                        .attr("d", `M${localSelectedNode.x},${localSelectedNode.y} L${localSelectedNode.x},${localSelectedNode.y}`)
                        .attr("stroke", localSelectedNode.data.color)
                        .attr("stroke-width", 3)
                        .attr("stroke-dasharray", "5,5")
                        .attr("fill", "none");
                        
                    // Store the start position for measuring drag distance
                    this.dragStartX = event.x;
                    this.dragStartY = event.y;
                }
            })
            .on("drag", function(event) {
                // Only proceed if we have a selected node
                if (!localSelectedNode) return;
                
                const dx = event.x - this.dragStartX;
                const dy = event.y - this.dragStartY;
                const dragDistance = Math.sqrt(dx*dx + dy*dy);
                
                // For non-touch events, we need to check drag distance
                if (!event.sourceEvent.type.startsWith('touch')) {
                    if (!this.dragActive && dragDistance > 5) {
                        this.dragActive = true;
                    }
                    if (!this.dragActive) return;
                }
                
                // Get the mouse position within the SVG
                const mousePoint = d3.pointer(event, svg.node());
                
                // Apply the inverse of the zoom transformation
                const transform = d3.zoomTransform(svg.node());
                const point = transform.invert(mousePoint);
                
                try {
                    // Update the temp connection line
                    const tempConnection = document.getElementById("temp-connection");
                    if (tempConnection) {
                        tempConnection.setAttribute("d", `M${localSelectedNode.x},${localSelectedNode.y} L${point[0]},${point[1]}`);
                    }
                    
                    // NODE DETECTION - Find if we're currently hovering over a node
                    const [x, y] = [point[0], point[1]];
                    let targetNode = null;

                    // More generous threshold for touch events
                    const threshold = nodeRadius * nodeRadius * (event.sourceEvent.type.startsWith('touch') ? 2.0 : 0.9);
                    
                    // First, reset all node highlights
                    graph.nodes().forEach(node => {
                        if (node !== localSelectedNode) {
                            const circleId = `#circle-${node.data.id}`;
                            d3.select(circleId)
                                .attr("fill", node.data.color)
                                .attr("stroke", "none")
                                .attr("stroke-width", 0);
                        }
                    });
                    
                    // Check each node
                    graph.nodes().forEach(node => {
                        // Skip the source node - can't connect to itself
                        if (node === localSelectedNode) return;

                        const dx = x - node.x;
                        const dy = y - node.y;
                        const distanceSquared = dx * dx + dy * dy;

                        // Check if we're directly over a node for highlighting
                        if (distanceSquared <= threshold) {
                            targetNode = node;
                            
                            // Highlight this node
                            const circleId = `#circle-${node.data.id}`;
                            d3.select(circleId)
                                .attr("fill", node.data.color)
                                .attr("stroke", "#3fc1ff")
                                .attr("stroke-width", event.sourceEvent.type.startsWith('touch') ? 12 : 8);
                            
                            // Add haptic feedback on mobile
                            if (event.sourceEvent.type.startsWith('touch') && navigator.vibrate && (!this.lastVibrateTime || 
                                (Date.now() - this.lastVibrateTime > 300))) {
                                navigator.vibrate(20);
                                this.lastVibrateTime = Date.now();
                            }
                        }
                    });
                } catch (err) {
                    console.error("Error in drag handler:", err);
                }
            })
            .on("end", function(event, d) {
                
                // Remove the temporary connection
                rootG.select("#temp-connection").remove();
                
                // Only process connections if we had an active drag
                if (!localSelectedNode || (!this.dragActive && !touchDragging)) return;
                
                // Find which node (if any) is under the mouse cursor at drag end
                const transform = d3.zoomTransform(svg.node());
                const [mouseX, mouseY] = d3.pointer(event, svg.node());
                const [x, y] = transform.invert([mouseX, mouseY]);
                
                // Check each node to see if the mouse is over it
                let targetNode = null;
                graph.nodes().forEach(node => {
                    const dx = x - node.x;
                    const dy = y - node.y;
                    // Check if pointer is within the node circle
                    if (dx * dx + dy * dy <= (nodeRadius * 0.9) * (nodeRadius * 0.9)) {
                        targetNode = node;
                    }
                });
                
                // If we found a target node that's not the source node, create connection
                if (targetNode && targetNode !== localSelectedNode) {
                    
                    // Get the CSRF token
                    const csrfToken = document.querySelector("[name=csrf-token]").content;
                    
                    // Find the target node data in the original data array
                    const targetNodeData = data.find(node => node.id === targetNode.data.id);
                    
                    if (targetNodeData) {
                        // Create updated node data with the new parent
                        const updatedNodeData = {
                            ...targetNodeData,
                            parentIds: [...(targetNodeData.parentIds || []), localSelectedNode.data.id]
                        };
                        
                        // Send API request to update the node
                        fetch("/dag_nodes/", {
                            method: "PUT",
                            headers: {
                                "Content-Type": "application/json",
                                "X-CSRF-Token": csrfToken,
                            },
                            body: JSON.stringify(updatedNodeData),
                        })
                        .then(response => {
                            if (response.ok) {
                                // Reload the page or update the UI as needed
                                window.location.reload();
                            } else if (response.status === 409) {
                                alert("Cannot create this connection due to a conflict.");
                            } else {
                                console.error("Failed to create connection");
                            }
                        })
                        .catch(error => {
                            console.error("Error:", error);
                        });
                    }
                    
                    // Clear selection after connecting
                    d3.selectAll('.node-selected')
                        .classed('node-selected', false)
                        .select('circle')
                        .attr('stroke-width', 0);
                    
                    localSelectedNode = null;
                    setSelectedNode(null);
                }
            });        
        // Function to filter nodes based on search text
        function filterNodesBySearch(searchText) {
            const searchQuery = searchText.toLowerCase().trim();
            
            if (!searchQuery) {
                // If search is empty, show all nodes and links
                svg.selectAll(".node-group").style("opacity", 1);
                svg.selectAll(".link-path").style("opacity", 1);
                svg.selectAll(".arrow-path").style("opacity", 1);
                return;
            }
            
            // Keep track of nodes that match the search
            const matchingNodeIds = new Set();
            
            // First pass: identify matching nodes
            graph.nodes().forEach(node => {
                const nodeName = node.data.name.toLowerCase();
                const nodeId = node.data.id;
                
                if (nodeName.includes(searchQuery)) {
                    matchingNodeIds.add(nodeId);
                }
            });
            
            // Second pass: show/hide nodes based on matches
            graph.nodes().forEach(node => {
                const nodeId = node.data.id;
                const nodeElement = svg.select(`.node-group-${nodeId}`);
                
                if (matchingNodeIds.has(nodeId)) {
                    nodeElement.style("opacity", 1);
                } else {
                    nodeElement.style("opacity", 0.2);
                }
            });
            
            // Handle links - only show links between visible nodes
            graph.links().forEach(link => {
                const sourceId = link.source.data.id;
                const targetId = link.target.data.id;
                const linkId = encodeURIComponent(`${sourceId}--${targetId}`);
                
                const sourceVisible = matchingNodeIds.has(sourceId);
                const targetVisible = matchingNodeIds.has(targetId);
                
                // Show links between visible nodes, fade others
                svg.selectAll(`.link-${linkId}`)
                    .style("opacity", (sourceVisible && targetVisible) ? 1 : 0.1);
                    
                svg.selectAll(`.arrow-${linkId}`)
                    .style("opacity", (sourceVisible && targetVisible) ? 1 : 0.1);
            });
        }
        
        // Add event listener for search input
        document.getElementById('dag-search-input').addEventListener('input', (e) => {
            filterNodesBySearch(e.target.value);
        });

        // Create nodes with proper handlers
        const nodes = svg
            .select("#nodes")
            .selectAll("g")
            .data(graph.nodes())
            .join("g")
            .attr("class", d => `cursor-pointer node-group node-group-${d.data.id}`) // Add the node-group-{id} class for search filtering
            .attr("transform", ({ x, y }) => `translate(${x}, ${y})`)
            .attr("opacity", 0)
            .on("mouseout", handleMouseOut)
            .on("click", handleNodeClick)
            .on("touchstart", handleNodeTouch)
            .call(dragHandler);
        
        // Add circles to nodes
        nodes.append("circle")
            .attr("id", (n) => `circle-${n.data.id}`)
            .attr("r", nodeRadius * 0.9)
            .attr("fill", (n) => n.data.color)
            .attr("stroke", "none");
        
        // Replace simple text with wrapped text
        nodes.each(function(d) {
            // Create text element
            const textElement = d3.select(this).append("text")
                .attr("text-anchor", "middle")
                .attr("fill", "white")
                .style("pointer-events", "none")
                .style("user-select", "none")
                .attr("font-size", "14px");
            
            // Get the node name
            const name = d.data.name;
            
            // Split text into multiple lines (wrap text)
            const words = name.split(/\s+/);
            let lines = [];
            let currentLine = '';
            const charsPerLine = Math.floor(nodeRadius / 4); // Adjust based on node size
            
            // Process words into lines
            words.forEach(word => {
                // Check if adding this word would exceed the line length
                if ((currentLine + ' ' + word).length > charsPerLine && currentLine !== '') {
                    lines.push(currentLine);
                    currentLine = word;
                } else {
                    currentLine = currentLine === '' ? word : currentLine + ' ' + word;
                }
            });
            
            // Add the last line if it's not empty
            if (currentLine !== '') {
                lines.push(currentLine);
            }
            
            // Add each line as a tspan element
            lines.forEach((line, i) => {
                textElement.append("tspan")
                    .attr("x", 0)
                    .attr("y", (i - (lines.length - 1) / 2) * 16) // Vertical position adjustment
                    .attr("dy", "0.35em") // Fine-tune vertical alignment
                    .text(line);
            });
        });
            
        // Animate nodes
        nodes.transition(trans).attr("opacity", 1);
            
        // Add background click handler to clear selection and detect double-clicks
        let lastBackgroundClickTime = 0;
        svg.on("click", function(event) {
            // Ignore if this is a click on a node (event already handled)
            if (event.defaultPrevented) return;
            
            // Clear all selections
            clearAllSelections();
            
            // Double-click detection
            const now = Date.now();
            if (now - lastBackgroundClickTime < 300) { // 300ms double-click threshold
                // Double-click detected - create new node
                if (typeof createNewNode === 'function') {
                    event.preventDefault(); // Prevent other handlers
                    createNewNode();
                }
                lastBackgroundClickTime = 0; // Reset to prevent triple-click
            } else {
                lastBackgroundClickTime = now;
            }
        });

        // Create gradients for links
        svg
            .select("#defs")
            .selectAll("linearGradient")
            .data(graph.links())
            .join("linearGradient")
            .attr("id", ({ source, target }) => 
                encodeURIComponent(`${source.data.id}--${target.data.id}`)
            )
            .attr("gradientUnits", "userSpaceOnUse")
            .attr("x1", ({ points }) => points[0][0])
            .attr("x2", ({ points }) => points[points.length - 1][0])
            .attr("y1", ({ points }) => points[0][1])
            .attr("y2", ({ points }) => points[points.length - 1][1])
            .each(function(d) {
                d3.select(this)
                    .append("stop")
                    .attr("class", "grad-start")
                    .attr("offset", "0%")
                    .attr("stop-color", ({ source }) => source.data.color);
                    
                d3.select(this)
                    .append("stop")
                    .attr("class", "grad-stop")
                    .attr("offset", "100%")
                    .attr("stop-color", ({ target }) => target.data.color);
            });

        // Create links with selection ability
        svg
            .select("#links")
            .selectAll("path")
            .data(graph.links())
            .join("path")
            .attr("d", ({ points }) => line(points))
            .attr("fill", "none")
            .attr("stroke-width", 3)
            .attr(
                "stroke",
                ({ source, target }) => `url(#${encodeURIComponent(`${source.data.id}--${target.data.id}`)})`
            )
            .attr("opacity", 0)
            .attr("class", ({ source, target }) => 
                `cursor-pointer link-path link-${encodeURIComponent(`${source.data.id}--${target.data.id}`)}`) // Add the link-{id} class for search filtering
            .style("pointer-events", "stroke") // Make the stroke area clickable
            .on("click", handleLinkClick) // Add click handler for links
            .transition(trans)
            .attr("opacity", 1);

        // Create arrows with clickable areas
        const arrowSize = 80;
        const arrowLen = Math.sqrt((4 * arrowSize) / Math.sqrt(3));
        const arrow = d3.symbol().type(d3.symbolTriangle).size(arrowSize);
        
        svg
            .select("#arrows")
            .selectAll("path")
            .data(graph.links())
            .join("path")
            .attr("d", arrow)
            .attr("class", ({ source, target }) => 
                `cursor-pointer arrow-path arrow-${encodeURIComponent(`${source.data.id}--${target.data.id}`)}`) // Add the arrow-{id} class for search filtering
            .attr("fill", ({ target }) => target.data.color)
            .attr("transform", arrowTransform)
            .attr("opacity", 0)
            .attr("stroke", "white")
            .attr("stroke-width", 2)
            .attr("stroke-dasharray", `${arrowLen},${arrowLen}`)
            .style("pointer-events", "all") // Make the entire arrow clickable
            .on("click", handleLinkClick) // Add click handler for arrows
            .transition(trans)
            .attr("opacity", 1);

        // Pan and zoom
        const zoom = d3.zoom()
            .on("zoom", (event) => {
                rootG.attr("transform", event.transform);
            })
            .scaleExtent([0.15, 2]);
        
        // Initial zoom to fit the content
        const graphBounds = rootG.node().getBBox();
        const containerWidth = document.getElementById("graph-container").clientWidth;
        const containerHeight = document.getElementById("graph-container").clientHeight;
        
        // Make sure we're using the full container height
        const scale = Math.min(
            containerWidth / graphBounds.width,
            containerHeight / graphBounds.height,
            1 // Don't zoom in more than 100%
        );
        
        // Center the graph initially but don't restrict panning
        const centerX = (containerWidth - graphBounds.width * scale) / 2;
        const centerY = (containerHeight - graphBounds.height * scale) / 2;
        
        svg.call(zoom)
           .call(
                zoom.transform, 
                d3.zoomIdentity
                  .translate(centerX, centerY)
                  .scale(scale)
            );
    }

    useEffect(() => {
        // Clear any existing visualization
        const container = document.getElementById("graph-container");
        if (container) {
            container.innerHTML = `
                <svg id="graph">
                    <g>
                        <defs id="defs" />
                        <g id="links" />
                        <g id="nodes" />
                        <g id="arrows" />
                    </g>
                </svg>
            `;
        }
        
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.src = d3link;
        script.onload = callback;
        document.head.appendChild(script);
        
        // Cleanup function
        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, [data, d3link, name]); // Add dependencies to rerender when these change

    // Handle search text change
    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        // The actual filtering is performed by the event listener in callback
    };

    return (
        <>
            {/* Create a container div for panning and zooming */}
            <div id="graph-container" style={{
                zIndex: 0, 
                position: "absolute",
                width: "100%", 
                height: "100%",
                top: 0,
                left: 0,
                bottom: 0,
                right: 0
            }}>
                {/* SVG will be injected here by the useEffect */}
            </div>

            {/* Title of system - Under so that it overlays */}
            <span id="diagramName" className="absolute text-white text-4xl top-12 right-12 cursor-pointer" style={{
                color: '#6A6E70',
                maxWidth: '50%',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto'
            }}></span>
            
            {/* Search Bar */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10 flex items-center">
                <div className="relative">
                    <input
                        id="dag-search-input"
                        type="text"
                        placeholder="Search nodes..."
                        value={searchText}
                        onChange={handleSearchChange}
                        className="px-4 py-2 w-64 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        style={{
                            backdropFilter: 'blur(5px)',
                            backgroundColor: 'rgba(0, 0, 0, 0.1)'
                        }}
                    />
                    {searchText && (
                        <button 
                            onClick={() => {
                                setSearchText('');
                                document.getElementById('dag-search-input').value = '';
                                document.getElementById('dag-search-input').dispatchEvent(new Event('input'));
                            }}
                            className="px-2 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>
            
            {/* Add CSS styles for links and arrows */}
            <style jsx="true" global="true">{`
                .link-path, .arrow-path {
                    cursor: pointer;
                }
                .link-selected {
                    stroke-width: 5px !important;
                    filter: drop-shadow(0 0 3px rgba(255,255,255,0.5));
                }
                #delete-link-button {
                    cursor: pointer;
                }
                .node-group, .link-path, .arrow-path {
                    transition: opacity 0.5s ease;
                }
            `}</style>
        </>
    );
};

export default DagDiagram;